/**
 * Theme Name: project-sscms v1.519.0
 * Theme URI: git+https://github.com/1debit/project-sscms.git
 * Author: Sharp Notions, LLC
 * Author URI: https://sharpnotions.com
 * Description: Wordpress theme for Chime (project-sscms) [main - b7cd30e04]
 * Version: 1.519.0-2025-08-13T14-18-27
 * License: ISC
 * License URI: "http://www.gnu.org/licenses/gpl-2.0.html"
 */
/**
 * File lazy-video.js.
 * Handles logic for the lazy loaded videos.
 */

let currentVideoItem = null;
let videoHtml = null;

// Helper function to escape HTML attribute values
function escapeHtmlAttribute(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function stripScriptTags(videoHtml) {
  if (typeof videoHtml !== 'string') return '';

  // Whitelist of trusted script domains
  const allowedDomains = [
    'www.youtube.com',
    'youtube.com',
    'www.youtube-nocookie.com',
    'www.tiktok.com',
    'tiktok.com',
  ];

  // First pass: Remove all script tags and their contents
  let scriptSrcs = [];
  let sanitizedHtml = videoHtml;
  
  // Keep removing script tags until none are left (handles nested scripts)
  let previousLength;
  do {
    previousLength = sanitizedHtml.length;
    sanitizedHtml = sanitizedHtml.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, function(match) {
      // Extract src attribute if present
      const srcMatch = match.match(/src=["']([^"']+)["']/i);
      if (srcMatch && srcMatch[1]) {
        try {
          const url = sscmsNewUrl(srcMatch[1], window.location.origin);
          if (allowedDomains.includes(url.hostname)) {
            scriptSrcs.push(url.href);
          }
        } catch (e) {
          console.warn('Invalid URL:', srcMatch[1]);
        }
      }
      return '';
    });
  } while (previousLength !== sanitizedHtml.length);

  // Second pass: Remove any remaining script-like content
  sanitizedHtml = sanitizedHtml
    .replace(/<script\b[^>]*>/gi, '') // Remove opening script tags
    .replace(/<\/script>/gi, '')     // Remove closing script tags
    .replace(/ on\w+="[^"]*"/gi, '') // Remove inline event handlers
    .replace(/javascript:/gi, '')    // Remove javascript: protocol
    .replace(/vbscript:/gi, '')      // Remove vbscript: protocol
    .replace(/data:text\/html/gi, '') // Remove data URLs
    .replace(/data:application\/javascript/gi, ''); // Remove JavaScript data URLs

  // Third pass: Remove any remaining potentially dangerous attributes
  sanitizedHtml = sanitizedHtml
    .replace(/<[^>]*(?:on\w+|javascript:)[^>]*>/gi, '') // Remove elements with event handlers
    .replace(/<[^>]*style=["'][^"']*expression\s*\([^"']*["'][^>]*>/gi, ''); // Remove style expressions

  // Safely parse and return sanitized HTML
  const tempDiv = document.createElement('div');
  
  // Encode HTML entities in attributes
  sanitizedHtml = sanitizedHtml.replace(/<([^>]*)([^>]*)>/g, function(match, tag, attrs) {
    // Encode attribute values
    attrs = attrs.replace(/(\w+)=["']([^"']*)["']/g, function(match, attr, value) {
      // Don't encode URLs in src/href attributes
      if (attr === 'src' || attr === 'href') {
        try {
          const url = sscmsNewUrl(value);
          if (allowedDomains.includes(url.hostname)) {
            return `${attr}="${escapeHtmlAttribute(value)}"`;
          }
        } catch (e) {
          // If URL parsing fails, encode the value
          return `${attr}="${escapeHtmlAttribute(encodeURIComponent(value))}"`;
        }
      }
      return `${attr}="${escapeHtmlAttribute(value)}"`;
    });
    return `<${tag}${attrs}>`;
  });

  // Inject only safe scripts
  scriptSrcs.forEach(src => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.classList.add('video-list-script');
    document.body.appendChild(script);
  });

  return sanitizedHtml.trim();
}

function toggleOverlay(videoItem, change) {
  switch (change) {
    case 'loading':
      videoItem.classList.remove('playing');
      videoItem.classList.add('loading');
      break;
    case 'show':
      videoItem.classList.remove('loading');
      videoItem.classList.add('playing');
      break;
    case 'hide':
      videoItem.classList.remove('playing', 'loading');
      break;
  }
}

function loadIframe(videoItem) {
  const slideIframe = videoItem.querySelector('iframe');

  // if iframe exists, wait for it to load, then hide overlay
  if (slideIframe) {
    slideIframe.addEventListener('load', function () {
      toggleOverlay(videoItem, 'show');
    });
  }
  
  // if iframe does not exist, just hide overlay
  else {
    toggleOverlay(videoItem, 'show');
  }
}

function removeIframe(videoItem) {
  if (!videoItem) videoItem = currentVideoItem;
  if (!videoItem) return;
  
  // check if there is a video to remove
  if (videoItem.querySelector('iframe')) {
    videoItem.removeChild(videoItem.lastChild);
  }

  toggleOverlay(videoItem, 'hide');

  // clear currentVideoItem - needed if called from another script
  if (videoItem == currentVideoItem) {
    currentVideoItem = null;
  }
}

function prepIframe(videoItem) {
  if (currentVideoItem) {
    removeIframe(currentVideoItem);
  }
  currentVideoItem = videoItem;

  videoHtml = stripScriptTags(videoItem.getAttribute('data-video'));
  videoItem.insertAdjacentHTML('beforeend', videoHtml);

  // prepare video for lazy loading
  const lazyNoscript = videoItem.querySelector('noscript.loading-lazy')
  if (typeof loadingAttributePolyfill !== 'undefined' && lazyNoscript) loadingAttributePolyfill.prepareElement(lazyNoscript);

  // replace play btn with loading icon
  toggleOverlay(videoItem, 'loading');

  // quick timeout to allow for iframe to be created in modal
  window.setTimeout(function () {
    loadIframe(videoItem);
  }, 300);
}

/**
 * Inits
 */
let lazyLoadedVideos = [];
function initLazyVideos(container = document) {
  if (typeof jQuery == 'undefined') {
    return;
  }

  const lazyVideos = container.querySelectorAll('.lazy-video');

  // if no lazy videos, return
  if (lazyVideos.length === 0) {
    return;
  }

  for (let i = 0; i < lazyVideos.length; i++) {
    // if video is already loaded, skip it
    if (lazyLoadedVideos.includes(lazyVideos[i])) continue;

    // add video to lazyLoadedVideos array
    lazyLoadedVideos.push(lazyVideos[i]);

    // add click event to load video
    lazyVideos[i].addEventListener('click', function (e) {
      sscmsPreventDefault(e);
      prepIframe(lazyVideos[i]);
    });
  }
}

initLazyVideos();
